'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import type { SortingState } from '@tanstack/react-table';
import { CursorPagination } from '@morpho-org/ui-v6/client';
import { TableNoResults } from '@morpho-org/ui-v6/server';
import { useTable } from '@tanstack/react-table';
import { useState } from 'react';
import { ErrorDataUnavailable } from '@/components/common/Error';
import { Table, TrBodyRow, TrHeaderGroup } from '@/components/common/Table';
import { TableScrollArea } from '@/components/common/Table/CompactTable/Table';
import { useSkeletonRows } from '@/hooks/useSkeletonRows';
import { marketBorrowersColumnsDefs, PAGE_SIZE } from './config';
import { marketBorrowersTableFeatures } from './config/MarketBorrowers.columns';
import { useMarketBorrowersData, useMarketBorrowersRow } from './hooks';
export interface MarketBorrowersClientProps {
  chainId: ChainId;
  marketId: string;
}
const MarketBorrowersClient = t0 => {
  const $ = _c(53);
  const {
    chainId,
    marketId
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [sorting, setSorting] = useState(t1);
  let t2;
  if ($[1] !== chainId || $[2] !== marketId || $[3] !== sorting) {
    t2 = {
      chainId,
      marketId,
      first: PAGE_SIZE,
      sorting
    };
    $[1] = chainId;
    $[2] = marketId;
    $[3] = sorting;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    page,
    items,
    partial,
    isLoading,
    isLoadingNextPage,
    error,
    pageIndex,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    prefetchNext
  } = useMarketBorrowersData(t2);
  let t3;
  if ($[5] !== chainId || $[6] !== items || $[7] !== marketId || $[8] !== pageIndex) {
    t3 = {
      items,
      chainId,
      marketId,
      currentPage: pageIndex
    };
    $[5] = chainId;
    $[6] = items;
    $[7] = marketId;
    $[8] = pageIndex;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const formattedData = useMarketBorrowersRow(t3);
  let t4;
  if ($[10] !== sorting) {
    t4 = {
      sorting
    };
    $[10] = sorting;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  let t5;
  if ($[12] !== formattedData || $[13] !== t4) {
    t5 = {
      features: marketBorrowersTableFeatures,
      data: formattedData,
      state: t4,
      manualSorting: true,
      columns: marketBorrowersColumnsDefs,
      enableSortingRemoval: false,
      sortDescFirst: true,
      onSortingChange: setSorting
    };
    $[12] = formattedData;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const marketBorrowersTable = useTable(t5);
  const loading = isLoading || isLoadingNextPage;
  let t6;
  if ($[15] !== marketBorrowersTable) {
    t6 = marketBorrowersTable.getRowModel();
    $[15] = marketBorrowersTable;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const emptyRows = t6.rows.length === 0;
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      first: PAGE_SIZE,
      totalCount: PAGE_SIZE,
      pagination: {
        pageIndex: 0,
        pageSize: PAGE_SIZE
      },
      columnsDefs: marketBorrowersColumnsDefs
    };
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const skeletonRows = useSkeletonRows(t7);
  let t8;
  if ($[18] !== emptyRows || $[19] !== error || $[20] !== loading || $[21] !== marketBorrowersTable || $[22] !== skeletonRows) {
    t8 = (loading || !emptyRows) && !error && <Table.Table tableVariant="compact"><Table.Thead>{marketBorrowersTable.getHeaderGroups().map(_temp)}</Table.Thead><Table.Tbody>{!loading && marketBorrowersTable.getRowModel().rows.map(_temp2)}{loading && skeletonRows.map(_temp3)}</Table.Tbody></Table.Table>;
    $[18] = emptyRows;
    $[19] = error;
    $[20] = loading;
    $[21] = marketBorrowersTable;
    $[22] = skeletonRows;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== emptyRows || $[25] !== error || $[26] !== loading) {
    t9 = !loading && !error && emptyRows && <TableNoResults text="No borrowers found." height="480px" style={{
      borderRadius: "0"
    }} />;
    $[24] = emptyRows;
    $[25] = error;
    $[26] = loading;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== error || $[29] !== loading) {
    t10 = !loading && error && <ErrorDataUnavailable minHeight="480px" style={{
      borderRadius: "0"
    }} />;
    $[28] = error;
    $[29] = loading;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t8 || $[33] !== t9) {
    t11 = <TableScrollArea className="grid h-full w-full rounded-[20px] [&_td:first-of-type]:w-1/2 [&_td:nth-of-type(2)]:w-auto">{t8}{t9}{t10}</TableScrollArea>;
    $[31] = t10;
    $[32] = t8;
    $[33] = t9;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] !== error || $[36] !== loading || $[37] !== partial) {
    t12 = partial && !loading && !error && <p className="text-secondary-foreground px-sm text-xs" data-testid="market-borrowers-partial-notice">Some borrowers could not be fully loaded, so this page may be incomplete.</p>;
    $[35] = error;
    $[36] = loading;
    $[37] = partial;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const t13 = page ? Math.ceil(page.countTotal / PAGE_SIZE) : undefined;
  let t14;
  if ($[39] !== canNextPage || $[40] !== canPreviousPage || $[41] !== error || $[42] !== loading || $[43] !== nextPage || $[44] !== pageIndex || $[45] !== prefetchNext || $[46] !== previousPage || $[47] !== t13) {
    t14 = <CursorPagination loading={loading} error={error} pageIndex={pageIndex} pageCount={t13} previousPage={previousPage} nextPage={nextPage} canPreviousPage={canPreviousPage} canNextPage={canNextPage} onPrefetchNext={prefetchNext} />;
    $[39] = canNextPage;
    $[40] = canPreviousPage;
    $[41] = error;
    $[42] = loading;
    $[43] = nextPage;
    $[44] = pageIndex;
    $[45] = prefetchNext;
    $[46] = previousPage;
    $[47] = t13;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t11 || $[50] !== t12 || $[51] !== t14) {
    t15 = <div className="gap-lg flex h-full w-full flex-col"><div className="gap-xs flex flex-col">{t11}{t12}{t14}</div></div>;
    $[49] = t11;
    $[50] = t12;
    $[51] = t14;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  return t15;
};
export default MarketBorrowersClient;
function _temp(headerGroup) {
  return <TrHeaderGroup key={headerGroup.id} headerGroup={headerGroup} />;
}
function _temp2(row) {
  return <TrBodyRow key={row.id} row={row} />;
}
function _temp3(row_0, index) {
  return <TrBodyRow key={index + "skeleton-table-market-borrowers"} skeletonRow={row_0} />;
}